import React, { useState, useEffect, useCallback } from "react";
import {
  Drawer, Row, Col, Tag, Typography, Divider, Collapse,
  Descriptions, Avatar, Space, Button, Tooltip,
  Spin, Alert, Badge, Modal, Table, Empty,
} from "antd";
import {
  FileExcelOutlined, UserOutlined, EyeOutlined,
  ClockCircleOutlined, InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  fetchUserSummary,
  fetchUserTraceJson,
  downloadUserTraceExcel,
} from "../../services/seguimiento/reporteSeguimientoServices";

const { Text, Title } = Typography;
const { Panel }       = Collapse;

// ── Mapeo de app_name → nombre amigable + descripción ─────────────────────
const APP_MAP = {
  inicio:                     { label: "Inicio",                    desc: "Panel principal de la plataforma" },
  "buscar-proyecto":          { label: "Actualización de Proyectos", desc: "Gestión y actualización de proyectos activos" },
  es_proyectos:               { label: "Estructuración",             desc: "Proyectos en fase de estructuración" },
  planOperativo:              { label: "Plan Operativo FAO",         desc: "Módulo de implementación FAO" },
  "menu-curso-fortalecimiento": { label: "Fortalecimiento",          desc: "Cursos y capacitación a beneficiarios" },
  participantes:              { label: "Participantes",              desc: "Gestión de participantes del proyecto" },
  novedad_participante:       { label: "Novedades",                  desc: "Novedades sobre participantes" },
  ecas_participante:          { label: "ECAs",                       desc: "Escuelas de campo agropecuario" },
  visita_participante:        { label: "Visitas",                    desc: "Registro de visitas a participantes" },
  organizaciones:             { label: "Organizacional",             desc: "Organizaciones vinculadas al proyecto" },
  "acuerdos-comerciales":     { label: "Comercial",                  desc: "Acuerdos y gestión comercial" },
  caracterizacion:            { label: "Caracterización",            desc: "Caracterización socio-económica de participantes" },
  sistematizacionIppta:       { label: "IPPTA",                      desc: "Índice de Práctica y Prosperidad Territorial Agropecuaria" },
  encuesta_EDAH:              { label: "Encuesta EDAH",              desc: "Encuesta de Diversidad Alimentaria en Hogares" },
  enfoqueGenero:              { label: "Enfoques Diferenciales",     desc: "Módulo de género y enfoque diferencial" },
  "planes-accion-fao":        { label: "Planes de Acción",           desc: "Planes de acción ejecutados" },
  poa:                        { label: "POA",                        desc: "Plan Operativo Anual — seguimiento documental" },
  reportes:                   { label: "Informes",                   desc: "Generación de informes del proyecto" },
  "informe-actas":            { label: "Actas de Entrega",           desc: "Informes de actas de entrega" },
  "poa-avance":               { label: "Avance POA",                 desc: "Exportable de informe de avance POA" },
  "poa-reporte-producto":     { label: "Reporte por Producto",       desc: "Reporte de productos POA" },
  dashboard_financiero:       { label: "Dashboard Financiero",       desc: "Panel de control de entregas e inventario" },
  productos_informe:          { label: "Informe de Productos",       desc: "Generación de informes de productos" },
  productos_financiero:       { label: "Productos",                  desc: "Catálogo de productos" },
  entregas_financiero:        { label: "Entregas",                   desc: "Control de entregas a beneficiarios" },
  estado_financiero:          { label: "Config. Estados",            desc: "Configuración de estados de productos" },
  proveedores_financiero:     { label: "Proveedores",                desc: "Gestión de proveedores" },
  unidad_medida_financiero:   { label: "Unidad de Medida",           desc: "Configuración de unidades de medida" },
  "reporte-seguimiento":      { label: "Log de Accesos",             desc: "Trazabilidad de accesos y auditoría de usuarios" },
  "app-slider":               { label: "Configuración General",      desc: "Config. general de la plataforma" },
  procesar_edah_admin:        { label: "Procesamiento Admin.",       desc: "Herramientas administrativas del sistema" },
};

const resolveApp = (appName) => APP_MAP[appName] || { label: "Módulo del sistema", desc: `Componente: ${appName}` };

// ── Mapeo sección → claves del trace ──────────────────────────────────────
const SECTION_TRACE = {
  seguridad: [
    { key: "historial_ingresos",  label: "Historial ingresos",  getFecha: r => r.fecha_ingreso,      getTipo: r => r.action_type,     getDesc: r => `IP: ${r.ip || "—"}  Dispositivo: ${r.dispositivo || "—"}` },
    { key: "log_legacy",          label: "Log legacy",           getFecha: r => r.fecha,              getTipo: r => r.action,          getDesc: r => r.description || "—" },
    { key: "acciones_plataforma", label: "Acciones plataforma",  getFecha: r => r.fecha,              getTipo: r => r.action,          getDesc: r => `${r.application}: ${r.description || "—"}` },
  ],
  cohortes: [
    { key: "cohortes",     label: "Cohortes",     getFecha: r => r.fechaRegistro,   getTipo: () => "REGISTRO",  getDesc: () => "Cohorte asignada" },
    { key: "cohortes_log", label: "Historial",    getFecha: r => r.fechaActualizacion, getTipo: r => r.tipo,    getDesc: () => "—" },
  ],
  documentos: [
    { key: "documentos_manifestacion",            label: "Subidos",    getFecha: r => r.fecha_documento,  getTipo: () => "SUBIDA",   getDesc: r => r.nameResource || "—" },
    { key: "documentos_manifestacion_eliminados", label: "Eliminados", getFecha: r => r.fecha_eliminacion, getTipo: () => "ELIMINADO", getDesc: r => `${r.nombre_archivo || "—"} ${r.observaciones ? "· " + r.observaciones : ""}` },
  ],
  edah: [
    { key: "edah_acciones", label: "EDAH log", getFecha: r => r.fecha, getTipo: r => r.accion, getDesc: r => `Medición: ${r.medicion || "—"}` },
  ],
  ecas: [
    { key: "ecas_participantes", label: "ECAs", getFecha: r => r.fecha_registro, getTipo: r => r.rol_usuario, getDesc: r => `ECA: ${r.nombreeca || "—"}` },
  ],
  enfoques: [
    { key: "enfoques_registrados", label: "Enfoques", getFecha: r => r.fecha, getTipo: () => "REGISTRO", getDesc: r => `${r.nombre || "—"} (Cal: ${r.calificacion ?? "—"})` },
  ],
  informes: [
    { key: "insumos_inf",  label: "Insumos",      getFecha: r => r.fecha_creacion, getTipo: () => "CREACIÓN",  getDesc: r => `${r.nombre_modulo} — ${r.periodo}` },
    { key: "visto_bueno",  label: "Vistos buenos", getFecha: r => r.fechaRegistro,  getTipo: () => "VISTO",     getDesc: r => r.nombre_modulo || "—" },
    { key: "inf_soportes", label: "Soportes",      getFecha: r => r.fecha_creacion, getTipo: () => "SOPORTE",   getDesc: r => r.nombre_soporte || "—" },
  ],
  ippta: [
    { key: "ippta_registros",     label: "Registros",    getFecha: r => r.fechaRegistro, getTipo: () => "REGISTRO",  getDesc: r => `${r.participante || "—"} — ${r.nombre_proyecto || ""}` },
    { key: "ippta_eliminaciones", label: "Eliminaciones", getFecha: r => r.fecha,         getTipo: () => "ELIMINADO", getDesc: r => `OID registro: ${r.registro_oid}` },
  ],
  novedades: [
    { key: "novedades",                    label: "Novedades",    getFecha: r => r.fecha_registro, getTipo: r => r.tipo_novedad,  getDesc: r => r.observacion || "—" },
    { key: "novedades_soportes",           label: "Soportes",     getFecha: r => r.fecha_registro, getTipo: r => r.tipo_soporte,  getDesc: r => r.nombre_real || "—" },
    { key: "novedad_historial_eliminados", label: "Eliminados",   getFecha: r => r.fecha,          getTipo: () => "ELIMINADO",    getDesc: () => "Registro eliminado del historial" },
  ],
  validaciones: [
    { key: "historico_validaciones", label: "Validaciones", getFecha: r => r.fecha_cambio, getTipo: r => r.campo, getDesc: r => `${r.valor_anterior || "—"} → ${r.valor_nuevo || "—"}` },
  ],
  mer: [
    { key: "auditoria_mer",          label: "Auditoría",        getFecha: r => r.fecha,          getTipo: r => r.campo_modificado, getDesc: r => `${r.valor_anterior} → ${r.valor_nuevo}` },
    { key: "soportes_mer",           label: "Soportes subidos", getFecha: r => r.fecha_creacion, getTipo: () => "SUBIDA",           getDesc: r => r.nombre_soporte || "—" },
    { key: "soportes_mer_historial", label: "Eliminados",       getFecha: r => r.fecha_eliminacion, getTipo: () => "ELIMINADO",     getDesc: r => r.nombre_soporte || "—" },
  ],
  poa: [
    { key: "poa_archivos",            label: "Archivos",    getFecha: r => r.fecha_creacion,    getTipo: () => "ARCHIVO",   getDesc: r => r.nombre_original || "—" },
    { key: "poa_documentos",          label: "Documentos",  getFecha: r => r.fecha_carga,        getTipo: r => r.typeAction, getDesc: r => r.nameResource || r.nombre_archivo || "—" },
    { key: "poa_documentos_historico",label: "Historial",   getFecha: r => r.fecha,             getTipo: () => "HISTORIAL", getDesc: () => "Cambio histórico en documento" },
    { key: "poa_eliminaciones",       label: "Eliminados",  getFecha: r => r.fechaEliminacion,  getTipo: () => "ELIMINADO", getDesc: r => r.observacion || "—" },
  ],
  pp: [
    { key: "pp_soportes", label: "Soportes PP", getFecha: r => r.fecha_creacion, getTipo: () => "SOPORTE", getDesc: r => `${r.nombre || "—"} — ${r.nombre_proyecto || ""}` },
  ],
  repositorio: [
    { key: "repo_documentos", label: "Documentos/Carpetas", getFecha: r => r.created_at, getTipo: r => r.type?.toUpperCase(),   getDesc: r => r.name || "—" },
    { key: "repo_favoritos",  label: "Favoritos",           getFecha: () => null,          getTipo: () => "FAVORITO",             getDesc: r => r.nombre_recurso || "—" },
    { key: "repo_historico",  label: "Historial acciones",  getFecha: r => r.fecha,       getTipo: r => r.accion,                getDesc: r => r.nombre_recurso || "—" },
  ],
  financiero: [
    { key: "fin_historico", label: "Financiero", getFecha: r => r.fecha, getTipo: r => r.operacion, getDesc: r => `${r.tabla}.${r.campo}: ${r.valor_anterior || "—"} → ${r.valor_nuevo || "—"}` },
  ],
  caracterizacion: [
    { key: "caracterizacion_temporal", label: "Caracterización", getFecha: r => r.created_at, getTipo: r => r.estado_procesamiento, getDesc: r => `${r.nombre || ""} ${r.apellido || ""} — ${r.nombre_proyecto || ""}` },
  ],
};

// ── Mapa completo de secciones para el Collapse ───────────────────────────
const SECCIONES = [
  { key: "seguridad",     icon: "🔐", label: "Seguridad y Sesiones",     fields: ["total_ingresos","total_log_legacy","total_acciones_plataforma"] },
  { key: "cohortes",      icon: "🎓", label: "Cohortes",                  fields: ["total_cohortes","total_cohortes_log"] },
  { key: "documentos",    icon: "📄", label: "Documentos Manifestación",  fields: ["total_documentos_manifestacion","total_documentos_manifestacion_eliminados"] },
  { key: "edah",          icon: "🌾", label: "EDAH",                      fields: ["total_edah_acciones"] },
  { key: "ecas",          icon: "🤝", label: "ECAs",                      fields: ["total_ecas_creadas","total_ecas_actualizadas","total_ecas_eliminadas"] },
  { key: "enfoques",      icon: "🎯", label: "Enfoques",                  fields: ["total_enfoques"] },
  { key: "informes",      icon: "📋", label: "Informes e Insumos",        fields: ["total_insumos_inf","total_visto_bueno","total_inf_soportes"] },
  { key: "ippta",         icon: "🌿", label: "IPPTA",                     fields: ["total_ippta","total_ippta_eliminaciones"] },
  { key: "novedades",     icon: "📝", label: "Novedades",                 fields: ["total_novedades","total_novedades_soportes","total_novedad_historial_eliminados"] },
  { key: "validaciones",  icon: "✅", label: "Validaciones POA",          fields: ["total_historico_validaciones"] },
  { key: "mer",           icon: "🏛️", label: "MER Organizaciones",        fields: ["total_auditoria_mer","total_soportes_mer","total_soportes_mer_eliminados"] },
  { key: "poa",           icon: "📁", label: "POA",                       fields: ["total_poa_archivos","total_poa_documentos","total_poa_documentos_historico","total_poa_eliminaciones"] },
  { key: "pp",            icon: "💳", label: "Planes de Pago",            fields: ["total_pp_soportes"] },
  { key: "repositorio",   icon: "🗂️", label: "Repositorio",               fields: ["total_repo_documentos","total_repo_favoritos","total_repo_historico"] },
  { key: "financiero",    icon: "💰", label: "Financiero",                fields: ["total_fin_historico"] },
  { key: "caracterizacion", icon: "📊", label: "Caracterización",         fields: ["total_caracterizacion_temp"] },
];

const FIELD_LABELS = {
  total_ingresos: "Ingresos",                              total_log_legacy: "Log legacy",
  total_acciones_plataforma: "Acciones plataforma",        total_cohortes: "Cohortes",
  total_cohortes_log: "Cambios cohortes",                  total_documentos_manifestacion: "Subidos",
  total_documentos_manifestacion_eliminados: "Eliminados", total_edah_acciones: "Acciones EDAH",
  total_ecas_creadas: "ECAs creadas",                      total_ecas_actualizadas: "Actualizadas",
  total_ecas_eliminadas: "Eliminadas",                     total_enfoques: "Enfoques",
  total_insumos_inf: "Insumos",                            total_visto_bueno: "Vistos buenos",
  total_inf_soportes: "Soportes",                          total_ippta: "Registros",
  total_ippta_eliminaciones: "Eliminaciones",              total_novedades: "Novedades",
  total_novedades_soportes: "Soportes",                    total_novedad_historial_eliminados: "Eliminados",
  total_historico_validaciones: "Validaciones",            total_auditoria_mer: "Auditoría MER",
  total_soportes_mer: "Soportes MER",                      total_soportes_mer_eliminados: "MER eliminados",
  total_poa_archivos: "Archivos POA",                      total_poa_documentos: "Docs medios",
  total_poa_documentos_historico: "Hist. docs",            total_poa_eliminaciones: "Eliminaciones",
  total_pp_soportes: "Soportes PP",                        total_repo_documentos: "Documentos",
  total_repo_favoritos: "Favoritos",                       total_repo_historico: "Historial",
  total_fin_historico: "Hist. financiero",                 total_caracterizacion_temp: "Caracterización",
};

const E_COLOR = { ART: "blue", FAO: "cyan", ADMIN: "purple" };
const E_BG    = { ART: "#dbeafe", FAO: "#ccfbf1", ADMIN: "#ede9fe" };
const E_TXT   = { ART: "#1d4ed8", FAO: "#0f766e", ADMIN: "#7c3aed" };
const fmt = (d) => d ? dayjs(d).format("DD/MM/YYYY HH:mm") : "—";

// ── Columnas del modal de registros ───────────────────────────────────────
const MODAL_COLS = [
  { title: "Origen",    dataIndex: "_origen",  key: "origen",  width: 150, ellipsis: true },
  { title: "Fecha",     dataIndex: "_fecha",   key: "fecha",   width: 140,
    render: (v) => v ? <Text style={{ fontSize: 12 }}><ClockCircleOutlined style={{ marginRight: 4, color: "#1677ff" }} />{fmt(v)}</Text> : <Text type="secondary">—</Text> },
  { title: "Tipo",      dataIndex: "_tipo",    key: "tipo",    width: 110,
    render: (v) => v ? <Tag color={v === "ELIMINADO" ? "orange" : v === "SUBIDA" || v === "ARCHIVO" ? "green" : "blue"} style={{ fontSize: 11 }}>{v}</Tag> : null },
  { title: "Descripción", dataIndex: "_desc",  key: "desc",    ellipsis: true,
    render: (v) => <Text style={{ fontSize: 12 }}>{v || "—"}</Text> },
];

// ── Componente principal ───────────────────────────────────────────────────
const DrawerDetalle = ({ userId, open, permissionExport, onClose, onExportTrace }) => {
  const [data,          setData]          = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [traceData,     setTraceData]     = useState(null);
  const [traceLoading,  setTraceLoading]  = useState(false);
  const [modalOpen,     setModalOpen]     = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [downloading,   setDownloading]   = useState(false);

  // Cargar resumen al abrir
  useEffect(() => {
    if (!userId || !open) { setData(null); setError(null); setTraceData(null); return; }
    setLoading(true);
    setError(null);
    fetchUserSummary(userId)
      .then((res) => { if (res.ok) setData(res.data); else setError(res.message); })
      .finally(() => setLoading(false));
  }, [userId, open]);

  // Limpiar trace cuando cambia el usuario
  useEffect(() => { setTraceData(null); }, [userId]);

  // Abrir modal de registros de sección
  const handleOpenSectionModal = useCallback(async (sectionKey) => {
    setActiveSection(sectionKey);
    setModalOpen(true);
    if (!traceData) {
      setTraceLoading(true);
      const res = await fetchUserTraceJson(userId);
      if (res.ok) setTraceData(res.data);
      setTraceLoading(false);
    }
  }, [traceData, userId]);

  // Construir registros para el modal de la sección activa
  const modalRecords = React.useMemo(() => {
    if (!traceData || !activeSection) return [];
    const map = SECTION_TRACE[activeSection] || [];
    return map.flatMap(({ key, label, getFecha, getTipo, getDesc }) =>
      (traceData[key] || []).map((r, i) => ({
        key:     `${key}-${i}`,
        _origen: label,
        _fecha:  getFecha ? getFecha(r) : null,
        _tipo:   getTipo  ? getTipo(r)  : null,
        _desc:   getDesc  ? getDesc(r)  : null,
      }))
    );
  }, [traceData, activeSection]);

  const handleDownload = async () => {
    setDownloading(true);
    await downloadUserTraceExcel(userId, data?.login);
    setDownloading(false);
  };

  const handleClose = () => { onClose(); setData(null); setError(null); setTraceData(null); setModalOpen(false); };

  const totalActividad = data
    ? Object.entries(data).filter(([k]) => k.startsWith("total_")).reduce((s, [, v]) => s + (Number(v) || 0), 0)
    : 0;

  const activeSec = SECCIONES.find((s) => s.key === activeSection);

  return (
    <>
      <Drawer
        title={
          <Space size={8}>
            <UserOutlined />
            <span>Detalle del usuario</span>
            {data?.entidad && (
              <Tag color={E_COLOR[data.entidad] || "default"} style={{ margin: 0 }}>
                {data.entidad}
              </Tag>
            )}
          </Space>
        }
        open={open}
        onClose={handleClose}
        width={660}
        styles={{ body: { padding: "20px 24px", background: "#fafafa" } }}
        extra={
          permissionExport && (
            <Tooltip title="Descarga trazabilidad completa en Excel (2 hojas: perfil + actividad)">
              <Button
                icon={<FileExcelOutlined />}
                onClick={handleDownload}
                loading={downloading}
                disabled={!data}
                style={{ background: "#52c41a", borderColor: "#52c41a", color: "#fff" }}
                size="small"
              >
                Exportar Excel
              </Button>
            </Tooltip>
          )
        }
      >
        {/* ── Estados ── */}
        {loading && (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <Spin size="large" tip="Cargando datos del usuario…" />
          </div>
        )}
        {error && !loading && (
          <Alert type="error" showIcon message="No se pudo cargar la información" description={error} style={{ borderRadius: 8 }} />
        )}

        {data && !loading && (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>

            {/* ── Perfil ── */}
            <div style={{ background: "#fff", borderRadius: 10, padding: "16px 18px", boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
              <Space size={14} align="start">
                <Avatar
                  size={52}
                  style={{ background: E_BG[data.entidad] || "#f0f0f0", color: E_TXT[data.entidad] || "#595959", fontSize: 22, fontWeight: 700, flexShrink: 0 }}
                >
                  {(data.name || data.login || "?")[0].toUpperCase()}
                </Avatar>
                <div style={{ flex: 1 }}>
                  <Title level={5} style={{ margin: 0, lineHeight: 1.3 }}>{data.name}</Title>
                  <Text type="secondary" style={{ fontSize: 12 }}>@{data.login}</Text>
                  {data.email && <div><Text type="secondary" style={{ fontSize: 11 }}>{data.email}</Text></div>}
                </div>
                <div>
                  {data.active === "Y"
                    ? <Badge status="success" text={<Text style={{ fontSize: 12 }}>Activo</Text>} />
                    : <Badge status="warning" text={<Text style={{ fontSize: 12 }}>Inactivo</Text>} />}
                </div>
              </Space>

              <Divider style={{ margin: "12px 0" }} />

              <Descriptions
                size="small" column={2} bordered
                labelStyle={{ fontSize: 12, fontWeight: 500, padding: "6px 10px", whiteSpace: "nowrap" }}
                contentStyle={{ fontSize: 12, padding: "6px 10px" }}
              >
                <Descriptions.Item label="Rol">
                  {data.role || <Text type="secondary">—</Text>}
                </Descriptions.Item>
                <Descriptions.Item label="MFA">
                  {data.mfa_habilitado
                    ? <Tag color="green" style={{ margin: 0 }}>Habilitado</Tag>
                    : <Tag color="orange" style={{ margin: 0 }}>Deshabilitado</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label={<><ClockCircleOutlined style={{ marginRight: 4 }} />Último ingreso</>}>
                  {data.ultimo_login ? dayjs(data.ultimo_login).format("DD/MM/YYYY HH:mm") : <Text type="secondary">—</Text>}
                </Descriptions.Item>
                <Descriptions.Item label="Última IP">
                  {data.ultima_ip || <Text type="secondary">—</Text>}
                </Descriptions.Item>
                <Descriptions.Item label="Total actividad" span={2}>
                  <strong style={{ color: "#1677ff", fontSize: 15 }}>{totalActividad}</strong>
                  <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                    acciones acumuladas en todos los módulos
                  </Text>
                </Descriptions.Item>
                {data.grupos?.length > 0 && (
                  <Descriptions.Item label="Grupos" span={2}>
                    <Space size={4} wrap>
                      {data.grupos.map((g) => <Tag key={g} style={{ margin: 0 }}>{g}</Tag>)}
                    </Space>
                  </Descriptions.Item>
                )}
                {data.apps_acceso?.length > 0 && (
                  <Descriptions.Item
                    label={
                      <Space size={4}>
                        <span>Apps con acceso</span>
                        <Tooltip title="Módulos del sistema a los que tiene acceso este usuario">
                          <InfoCircleOutlined style={{ color: "#1677ff", fontSize: 12 }} />
                        </Tooltip>
                      </Space>
                    }
                    span={2}
                  >
                    <Space size={4} wrap>
                      {data.apps_acceso.map((appName) => {
                        const { label, desc } = resolveApp(appName);
                        return (
                          <Tooltip key={appName} title={desc} placement="top">
                            <Tag color="geekblue" style={{ margin: 0, fontSize: 11, cursor: "default" }}>
                              {label}
                            </Tag>
                          </Tooltip>
                        );
                      })}
                    </Space>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>

            {/* ── Actividad por módulo ── */}
            <div style={{ background: "#fff", borderRadius: 10, padding: "16px 18px", boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
              <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.6px", fontWeight: 600 }}>
                Actividad por módulo
              </Text>
              <Divider style={{ margin: "8px 0 12px" }} />

              <Collapse ghost size="small" defaultActiveKey={["seguridad"]}>
                {SECCIONES.map((s) => {
                  const secTotal = s.fields.reduce((sum, f) => sum + (Number(data[f]) || 0), 0);
                  return (
                    <Panel
                      key={s.key}
                      style={{ marginBottom: 4, borderRadius: 6, border: "1px solid #f0f0f0" }}
                      header={
                        <Space size={6}>
                          <span style={{ fontSize: 13 }}>{s.icon} {s.label}</span>
                          {secTotal > 0 && (
                            <Tag color="blue" style={{ fontSize: 10, padding: "0 5px", margin: 0 }}>{secTotal}</Tag>
                          )}
                        </Space>
                      }
                      extra={
                        secTotal > 0 && (
                          <Tooltip title="Ver registros de este módulo">
                            <Button
                              type="link" size="small"
                              icon={<EyeOutlined />}
                              onClick={(e) => { e.stopPropagation(); handleOpenSectionModal(s.key); }}
                              style={{ fontSize: 11, padding: "0 6px", height: "auto" }}
                            >
                              Ver registros
                            </Button>
                          </Tooltip>
                        )
                      }
                    >
                      <Row gutter={[12, 12]}>
                        {s.fields.map((field) => {
                          const val = Number(data[field]) || 0;
                          return (
                            <Col xs={12} sm={8} key={field}>
                              <div style={{ textAlign: "center", padding: "6px 4px" }}>
                                <div style={{ fontSize: 22, fontWeight: 700, color: val > 0 ? "#1677ff" : "#d9d9d9", lineHeight: 1.2 }}>
                                  {val}
                                </div>
                                <Text type="secondary" style={{ fontSize: 11, display: "block", lineHeight: 1.3, marginTop: 2 }}>
                                  {FIELD_LABELS[field] || field}
                                </Text>
                              </div>
                            </Col>
                          );
                        })}
                      </Row>
                    </Panel>
                  );
                })}
              </Collapse>
            </div>

          </Space>
        )}
      </Drawer>

      {/* ── Modal de registros de sección ── */}
      <Modal
        open={modalOpen}
        title={
          <Space>
            {activeSec ? `${activeSec.icon} ${activeSec.label}` : "Registros"}
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
              — @{data?.login}
            </Text>
            {!traceLoading && (
              <Tag style={{ fontSize: 11, margin: 0 }}>{modalRecords.length} registros</Tag>
            )}
          </Space>
        }
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={800}
        styles={{ body: { padding: "12px 16px" } }}
      >
        {traceLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" tip="Cargando registros del módulo…" />
          </div>
        ) : modalRecords.length === 0 ? (
          <Empty description="Sin registros para este módulo" style={{ padding: "40px 0" }} />
        ) : (
          <Table
            columns={MODAL_COLS}
            dataSource={modalRecords}
            size="small"
            scroll={{ x: 640, y: 400 }}
            pagination={{ pageSize: 20, showSizeChanger: false, showTotal: (t) => `${t} registros` }}
            rowClassName={(_, i) => i % 2 === 0 ? "" : "seguimiento-modal-alt"}
          />
        )}
        <style>{`.seguimiento-modal-alt > td { background: #fafafa !important; }`}</style>
      </Modal>
    </>
  );
};

export default DrawerDetalle;
